import { ReactElement, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useFetch } from "../../hooks/useFetch";
import VeriticalCenter from "../../components/atoms/VerticalCenter";
import Container from "../../components/atoms/Container";

import DisplayMountain from "../../components/organisms/Home/DisplayMountain";
import { useResonanceLayout } from "../../components/organisms/ResonnanceLayout/ResoncanceContext";
import { postUserGamification } from "../../services/user.service";
import PointModal from "../Profile/PointsModal";
import { ComponentType, QuestionnaireDetailsType } from "../../types/Questionnaire";
import { getQuestionnairesByComponents } from "../../services/questionnaire.service";

import { useHistory, useParams } from "react-router";
import StoryTelling from "../../components/molecules/Home/StoryTellingModal";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import Info from "../../components/atoms/ErrorComponent";
import DisplayThankYouParticipation from "../../components/organisms/Invitation/DisplayThankYouParticipation";
import { UserGamificationDetails } from "../../types/welcome";
import { isMandatoryQuestionnaire } from "../../helper/questionnaire";
import { holdUpdateReload } from "../../serviceWorkerRegistration";

const LoguedHome = (): ReactElement => {
  const { setResonanceLayout, userGamificationDetails, questionnaires } = useResonanceLayout();  
  const history = useHistory();
  const { study } = useConfig();
  const { study_slug } = useParams<{ study_slug: string }>();
  const { user } = useAuth();
  const {
    t,
    i18n: { language: lang },
  } = useTranslation();

  const [pointToDisplay, setPointToDisplay] = useState(0);
  const [animationCompleted, setAnimationCompleted] = useState(false);

  const [isPointModalOpen, SetIsPointModalOpen] = useState(false);
  const [isThankYouParticipationModalOpen, SetIsThankYouParticipationModalOpen] = useState(false);
  const [isStorryTellingModalOpen, SetIsStorryTellingModalOpen] = useState(false);

  const [disableHeaderFinished, setDisableHeaderFinished] = useState(false);

  const [storyTellingFinished, setStoryTellingFinished] = useState(false);

  // Guard so the mandatory-questionnaire redirect can only fire once.
  const hasRedirectedRef = useRef(false);
  // Tracks whether this page is still mounted, so overlay callbacks that fire
  // during unmount don't setState on an unmounted component.
  const mountedRef = useRef(true);
  // Mirrors whether the storytelling modal is open/opening, read by the unmount
  // safety net (the <ion-modal> element may not exist yet at unmount time).
  const storyOpenRef = useRef(false);

  const {
    isError: isError,
    isLoading: isLoading,
    data: questionnairesBDD,
  } = useFetch<ComponentType[]>("questionnairesBDD", getQuestionnairesByComponents, {
    studyId: study.id,
    userId: user.id,
    lang: lang,
  });

  useEffect(() => {
    setResonanceLayout({
      disableHeader: true,
      disableNav: false,
      backgroundColor: "white",
    });
    
    setDisableHeaderFinished(true);
    
    return () =>
      setResonanceLayout({
        disableHeader: true,
        disableNav: false,
        backgroundColor: "white",
      });
  }, []);

  // Track mount state so late overlay callbacks don't setState after unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Keep the open/opening flag current for the unmount safety net below.
  useEffect(() => {
    storyOpenRef.current = isStorryTellingModalOpen;
  }, [isStorryTellingModalOpen]);

  // Safety net for unmounting (e.g. a bottom-nav tab tap) while the storytelling
  // modal is open OR still being presented. Ionic only dismisses on unmount if
  // the overlay element already exists; if the user navigates during the brief
  // async present() window (e.g. tapping a tab right after reload, before the
  // modal appears), the <ion-modal> is created/presented AFTER this component
  // (its controller) has unmounted, so nothing ever dismisses it and the
  // <ion-backdrop> lingers forever. We remove any storytelling overlay present
  // now, and briefly watch <ion-app> to catch one that appears late.
  useEffect(() => {
    return () => {
      // Dismiss (properly — so Ionic restores the root aria-hidden + scroll lock
      // and removes the element and its backdrop) any storytelling overlay that
      // is, or becomes, attached to <ion-app>. Returns true once none remain.
      const dismissOrphans = (): boolean => {
        const modals = Array.from(
          document.querySelectorAll<HTMLIonModalElement>("ion-modal.storytelling-modal")
        );
        modals.forEach((m) => {
          m.dismiss?.().catch(() => {});
        });
        return modals.length === 0;
      };

      // Dismiss any orphan that is already attached.
      dismissOrphans();

      // If the modal wasn't open/opening when we unmounted, nothing can appear
      // late — we're done (this is the normal skip -> redirect flow).
      if (!storyOpenRef.current) return;

      // It was open or still being presented: the <ion-modal> may be created and
      // presented a few frames AFTER this unmount, so keep trying until it has
      // presented and can be dismissed. Bounded so we never poll forever.
      let interval = 0;
      const finish = () => {
        window.clearInterval(interval);
        // Belt-and-suspenders: if no overlay remains, make sure the router
        // outlet isn't left inert (aria-hidden) after a late present().
        const anyOverlay = document.querySelector(
          "ion-app ion-modal, ion-app ion-popover, ion-app ion-action-sheet, ion-app ion-alert"
        );
        if (!anyOverlay) {
          document
            .querySelectorAll('ion-app ion-router-outlet[aria-hidden="true"]')
            .forEach((outlet) => outlet.removeAttribute("aria-hidden"));
        }
      };
      interval = window.setInterval(() => {
        if (dismissOrphans()) finish();
      }, 100);
      window.setTimeout(finish, 3000);
    };
  }, []);

  useEffect(() => {
    const UpdateUserGamification = async () => {
      try {
        const { data: userGamificationBDD } = await postUserGamification(study.id, user.id, null);

        if (userGamificationBDD){
          const userGamificationUpdates: UserGamificationDetails = {
            points: userGamificationBDD.points,
            step: userGamificationBDD.step,
            animation_success: userGamificationBDD.animation_success,
            pointsEarned: 0,
            stepEarned: 0,
            animation_successEarned: 0
          };
    
          setResonanceLayout({ userGamificationDetails: userGamificationUpdates });
        }
      }
      catch (e) 
      {
        console.error("Error Get UserGamification :" + e);
      }
    }

    if (userGamificationDetails != undefined)
    {
      if (userGamificationDetails.pointsEarned != 0 || userGamificationDetails.stepEarned != 0 || userGamificationDetails.animation_successEarned != 0)
      {
        checkPointEarned();
      }
      
      checkStoryTelling();
    }
    else
    {
      UpdateUserGamification();
    }

    return () => {};
  }, [userGamificationDetails]);

  useEffect(() => {
    if(questionnairesBDD)
    {
      if (questionnaires === undefined)
        setResonanceLayout({ questionnaires: questionnairesBDD });
    }

    return () => {};
  }, [questionnairesBDD]);

  const checkPointEarned = () => {
    if (userGamificationDetails.pointsEarned == 0)
    {
      setPointToDisplay(0);
    }
    else
    {
      setPointToDisplay(userGamificationDetails.pointsEarned);

      if (userGamificationDetails.animation_success == 0)
        SetIsThankYouParticipationModalOpen(true);
    }
  }

  const checkStoryTelling = () => {
    if(userGamificationDetails.points == 0 && userGamificationDetails.step == 0 && userGamificationDetails.animation_success == 0)
    {
      SetIsStorryTellingModalOpen(true);
    }
    else 
    {
      SetIsStorryTellingModalOpen(false);
      setStoryTellingFinished(true);
    }
  }

  const goQuestionnaire = (sequence_id: number, component_id: number) => {
    // Navigate at most once. `storyTellingFinished` (the trigger for this) is
    // now only set from the modal's onDidDismiss, so by the time we get here the
    // storytelling modal is already fully dismissed and its backdrop removed —
    // no requestAnimationFrame guessing needed.
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    // Close any remaining modals owned by this component before navigating.
    SetIsStorryTellingModalOpen(false);
    SetIsPointModalOpen(false);
    SetIsThankYouParticipationModalOpen(false);

    history.replace(`/${study_slug}/resonance/sequences/${sequence_id}/components/${component_id}`);
  };

  /** 
   * Check if there are mandatory questionnaires
   * */ 
  useEffect(() => {
    if (questionnaires && storyTellingFinished && userGamificationDetails)
    {
      for (const index in questionnaires) 
      {
        const questionnaire = questionnaires[index] as ComponentType;
  
        if (isMandatoryQuestionnaire(questionnaire.keywords) && (storyTellingFinished || userGamificationDetails.step !== 0 || userGamificationDetails.points !== 0)) 
        {
          goQuestionnaire(questionnaire.sequenceId, questionnaire.id);
          break;
        }
      }
    }
  }, [isLoading, questionnaires, storyTellingFinished, userGamificationDetails]);  

  const handleAnimationComplete = (value) => {
    setAnimationCompleted(value);
  };

  // Runs after the storytelling modal has FULLY dismissed (leave animation done,
  // backdrop removed). Only then do we mark it finished, which is what allows
  // the mandatory-questionnaire redirect to fire — so navigation can never race
  // the modal's teardown.
  const animation = () => {
    if (!mountedRef.current) return;
    SetIsStorryTellingModalOpen(false);
    setStoryTellingFinished(true);
  };

  useEffect(() =>
  {
    if (animationCompleted && pointToDisplay > 0)
      SetIsPointModalOpen(true);
  }, [animationCompleted, pointToDisplay]);

  // The earned points only live in memory: keep an app update from reloading the
  // page while the thank-you screen, the animation or the points modal is shown.
  const rewardShown = isThankYouParticipationModalOpen || isPointModalOpen;
  useEffect(() => {
    if (!rewardShown) return undefined;
    return holdUpdateReload();
  }, [rewardShown]);

  // Some rewards play no animation (e.g. mandatory questionnaires), so
  // animationCompleted may never turn true: hold only as long as an animation lasts.
  const rewardAnimating = pointToDisplay > 0 && !animationCompleted && !isThankYouParticipationModalOpen;
  useEffect(() => {
    if (!rewardAnimating) return undefined;
    return holdUpdateReload(10 * 1000);
  }, [rewardAnimating]);

  if (isLoading && questionnaires == undefined) return <ResonanceLoader />;

  if (isError)
  {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );
  }

  // Ajout Synakene 
  return (
    <Container>
      <VeriticalCenter>
      {isThankYouParticipationModalOpen ? (
        <DisplayThankYouParticipation onDismiss={() => SetIsThankYouParticipationModalOpen(false)} />
      ) : (
        <>
          <PointModal isOpen={isPointModalOpen} onDismiss={() => SetIsPointModalOpen(false)} points={pointToDisplay} />
          <StoryTelling
            isOpen={isStorryTellingModalOpen}
            onClose={() => SetIsStorryTellingModalOpen(false)}
            onDidDismiss={animation}
          />
          <DisplayMountain onAnimationComplete={handleAnimationComplete} disableHeaderFinished={disableHeaderFinished} />
        </>
      )}
      </VeriticalCenter>
    </Container>
  );
};

export default LoguedHome;